import { Component, Prop, Vue } from 'vue-property-decorator';
import { ASSESSMENT_PERIOD_STATUSES, EVALUATION_STATUSES, PAGE_NAMES, PROJECT_TYPES } from '@/constants';
import { convertNumberToFourDigitsString } from '@/helpers';
import { DisplayStatus, ProjectDTO, TableHeaderColumns } from '@/models';
import { sortArrByNumberField, sortArrByTextField, sortArrByUserDTOField, stopEvaluationProject } from '@/services';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import Status from '@/components/common/status/Status.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import ProjectManagerInformation from '@/components/pages/project-evaluation/project-information/project-manager-information/ProjectManagerInformation.vue';
import { Getter } from '@/decorators';

@Component({
  components: {
    ConfirmationDialog,
    InlineLoader,
    Status,
    Tooltip,
    UserInformation,
    Pagination,
    ProjectManagerInformation
  }
})
export default class AssessmentPeriodsList extends Vue {
  @Getter('project/managers') managers!: ProjectDTO[];

  @Prop({ default: () => [] }) private projects: ProjectDTO[];
  @Prop({ default: true }) private isProjectFetching: boolean;
  @Prop({ default: -1 }) private assessmentPeriodId: number;
  @Prop({ default: '' }) private assessmentPeriodStatus: string;

  private ON_GOING = ASSESSMENT_PERIOD_STATUSES.ON_GOING.label;
  private isStopEvaluationDialog = false;
  private projectNameStopEvaluation = '';
  private projectIdStopEvaluation = -1 as number;
  private headers: TableHeaderColumns = [
    { text: 'Project ID', align: 'start', value: 'id' },
    { text: 'Project name', value: 'name', align: 'start' },
    { text: 'Sponsor', value: 'sponsor', align: 'start' },
    { text: 'PM', value: 'manager', align: 'start' },
    { text: 'Project pollens', value: 'projectDrops', align: 'end' },
    { text: 'Evaluation status', value: 'projectStatus', align: 'start' },
    { text: '', value: 'manageProjects', align: 'end', sortable: false }
  ];
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  getDisplayStatus(status: string): DisplayStatus {
    if (this.assessmentPeriodStatus === EVALUATION_STATUSES['DONE'].label) {
      return EVALUATION_STATUSES['DONE'];
    }
    return EVALUATION_STATUSES[status];
  }

  customSort(items: ProjectDTO[], fields: string[], isDescArr: boolean[]): ProjectDTO[] {
    const field = fields[0];
    if (field) {
      const isDesc = isDescArr[0];
      switch (field) {
        case 'id': {
          return sortArrByNumberField(items, field, isDesc);
        }
        case 'name':
        case 'projectStatus': {
          return sortArrByTextField(items, field, isDesc);
        }
        case 'sponsor':
        case 'manager': {
          return sortArrByUserDTOField(items, field, isDesc);
        }
        default: {
          return items;
        }
      }
    }
    return items;
  }

  displayID(id: number): string {
    return convertNumberToFourDigitsString(id);
  }

  openStopEvaluationDialog(projectName: string, projectId: number): void {
    this.projectNameStopEvaluation = projectName;
    this.projectIdStopEvaluation = projectId;
    this.isStopEvaluationDialog = true;
  }

  isProjectOnGoing(project: ProjectDTO): boolean {
    return project.projectStatus !== EVALUATION_STATUSES.DONE.label && this.assessmentPeriodStatus === this.ON_GOING;
  }

  async stopEvaluationProjectById(id: number): Promise<void> {
    this.isStopEvaluationDialog = false;
    await stopEvaluationProject(id);
    this.$emit('onRefetchData');
  }

  async navigateToProjectEvaluation(id: number): Promise<void> {
    await this.$router.push({
      name: PAGE_NAMES.PROJECT_EVALUATION,
      params: {
        id: '' + id,
        assessmentPeriodId: this.assessmentPeriodId.toString()
      }
    });
  }

  getProjectDrops(project: ProjectDTO): string {
    const assessmentPeriodId: string = this.assessmentPeriodId.toString();
    const { projectAssessments } = project;
    const projectAssessment = projectAssessments.find(asm => asm.assessmentPeriodId === parseInt(assessmentPeriodId));

    if (project.projectType !== PROJECT_TYPES.INDIVIDUAL_TRACKING) {
      return projectAssessment?.finalDrops ? projectAssessment?.finalDrops.toString() : 'pending...';
    } else {
      let drops = 0;
      if (projectAssessment) {
        drops = projectAssessment.userEngagements.reduce((prev, userEngagement) => {
          return userEngagement.individualDrops ? userEngagement.individualDrops + prev : prev;
        }, 0);
      }
      return drops === 0 ? 'pending...' : drops.toString();
    }
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }
}
